/**
 * Computes a deep diff between two objects. Returns a nested object
 * containing only the fields that changed. Arrays are compared atomically
 * (any difference = entire array included in diff).
 *
 * Returns undefined if no changes detected.
 */
export function deepDiff(
  original: Record<string, any> | null | undefined,
  current: Record<string, any> | null | undefined,
): Record<string, any> | undefined {
  if (!current) return undefined;
  if (!original) return current;

  const diff: Record<string, any> = {};

  for (const key of Object.keys(current)) {
    const changed = diffValue(original[key], current[key], !(key in original));
    if (changed !== UNCHANGED) {
      diff[key] = changed;
    }
  }

  return Object.keys(diff).length > 0 ? diff : undefined;
}

/** Sentinel value indicating no change was detected. */
const UNCHANGED = Symbol('unchanged');

/**
 * Compare two values and return the new value if changed, or UNCHANGED sentinel.
 */
function diffValue(origVal: any, currVal: any, isNew: boolean): any {
  if (isNew) return currVal;

  // Both are plain objects - recurse
  if (isPlainObject(origVal) && isPlainObject(currVal)) {
    const nested = deepDiff(origVal, currVal);
    return nested !== undefined ? nested : UNCHANGED;
  }

  // Both are arrays - compare atomically via JSON
  if (Array.isArray(origVal) && Array.isArray(currVal)) {
    return JSON.stringify(origVal) !== JSON.stringify(currVal) ? currVal : UNCHANGED;
  }

  // Primitive or type-changed comparison
  return origVal !== currVal ? currVal : UNCHANGED;
}

function isPlainObject(val: unknown): val is Record<string, any> {
  return val !== null && typeof val === 'object' && !Array.isArray(val);
}
